/**
 * Le contrat de retour d'un détour — chunk 9.BA.1.
 *
 * ## Le défaut réparé
 *
 * Créer une entreprise depuis l'assistant de dossier sortait du parcours et n'y
 * ramenait pas : `Step1Company` posait un lien vers `/companies/new`, et
 * `CompanyCreatePage` terminait sur la fiche de l'entreprise. Le courtier pilote a
 * dû refaire le chemin à la main — Projets → Nouveau projet → rechercher son
 * client — alors qu'il venait de le créer.
 *
 * ## Pourquoi une liste blanche, et pas une simple validation « c'est interne »
 *
 * Un paramètre de retour non borné est une **redirection ouverte** : il suffit
 * d'envoyer un lien `…?retour=https://ailleurs` pour faire rebondir un
 * utilisateur authentifié hors du produit, avec la confiance que lui inspire
 * notre domaine. Vérifier « ça commence par `/` » ne suffit pas — `//ailleurs.test`
 * commence par `/` et le navigateur y voit une URL absolue de schéma relatif.
 *
 * On n'accepte donc **pas** un chemin arbitraire : on accepte une **destination
 * connue**, et on reconstruit l'URL nous-mêmes. Ce qui ne figure pas dans cette
 * liste n'a pas de retour — et le comportement retombe sur celui d'avant.
 */

/**
 * 9.BB.1 — une seconde destination, et pourquoi elle a besoin d'un identifiant.
 *
 * Revenir à l'assistant de dossier, c'est revenir à un chemin fixe. Revenir à la
 * préparation d'une demande, c'est revenir à **une** demande précise, dont
 * l'identifiant ne peut venir que de l'appelant. C'est exactement ce que la
 * liste blanche existe pour ne pas concaténer aveuglément.
 *
 * La règle reste donc la même, resserrée d'un cran : le chemin vient de la
 * table, et le seul fragment variable admis est un **UUID canonique**. Un UUID
 * ne peut contenir ni `/`, ni `?`, ni `#`, ni `//` — il ne peut donc pas sortir
 * du segment qu'on lui réserve, ni transformer le chemin en URL absolue. Tout ce
 * qui n'est pas un UUID ne produit **aucune** URL : `construireRetour` rend
 * `null`, et l'appelant retombe sur son comportement par défaut.
 */
const EST_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type Destination = {
  /** Le chemin, construit ici et nulle part ailleurs. `id` est déjà validé. */
  readonly chemin: (id: string) => string;
  /** Le chemin a-t-il besoin d'un identifiant pour exister ? */
  readonly exigeId: boolean;
};

/** Les seules destinations de retour du produit. */
const DESTINATIONS = {
  /** L'assistant de création de dossier. */
  "projet-nouveau": { chemin: () => "/projects/new", exigeId: false },
  /** La préparation d'une demande de soumission. */
  "demande-preparer": {
    chemin: (id: string) => `/quote-requests/${id}/preparer?section=specification`,
    exigeId: true,
  },
  /**
   * Le même écran, mais en RETOUR SEUL — chunk formulaires requis.
   *
   * `demande-preparer` ne dit pas seulement « reviens ici » : il dit « ce que tu
   * remplis EST le cahier de charges », et la soumission le rattache à la
   * demande. C'est juste quand on remplit le cahier ; c'est faux quand on remplit
   * un formulaire simplement EXIGÉ par le dossier — un questionnaire client
   * deviendrait alors ce que l'assureur reçoit, et la pièce jointe de l'envoi.
   *
   * Cette destination ramène au même endroit sans rien rattacher.
   */
  "demande-preparer-retour": {
    chemin: (id: string) => `/quote-requests/${id}/preparer`,
    exigeId: true,
  },
} as const satisfies Record<string, Destination>;

export type DestinationRetour = keyof typeof DESTINATIONS;

export function estDestinationConnue(valeur: string | null): valeur is DestinationRetour {
  return valeur !== null && Object.prototype.hasOwnProperty.call(DESTINATIONS, valeur);
}

/**
 * Reconstruit l'URL de retour, ou `null` si elle ne peut pas l'être sûrement.
 *
 * **Rien de ce que l'appelant fournit n'est concaténé au chemin** : le chemin
 * vient de la table, le seul fragment variable est un UUID validé, et seuls des
 * paramètres connus s'y ajoutent, encodés.
 */
export function construireRetour(opts: {
  destination: DestinationRetour;
  /** L'entreprise qui vient d'être créée, à sélectionner au retour. */
  companyId?: string;
  /** L'intention du parcours, préservée à travers le détour. */
  intention?: string | null;
  /** La ressource à laquelle revenir, pour les destinations qui en visent une. */
  id?: string | null;
}): string | null {
  const dest: Destination = DESTINATIONS[opts.destination];
  let chemin: string;
  if (dest.exigeId) {
    if (!opts.id || !EST_UUID.test(opts.id)) return null;
    chemin = dest.chemin(opts.id);
  } else {
    chemin = dest.chemin("");
  }

  const params = new URLSearchParams();
  if (opts.companyId) params.set("company_id", opts.companyId);
  // L'intention voyage avec le retour : sans elle, un détour par la création
  // d'entreprise transformerait silencieusement une « nouvelle demande » en
  // « nouveau projet », et le parcours s'arrêterait avant l'envoi.
  if (opts.intention) params.set("intention", opts.intention);
  const qs = params.toString();
  return qs ? `${chemin}?${qs}` : chemin;
}

/**
 * Le lien du détour : où aller, et comment revenir.
 *
 * L'intention est portée par le lien ALLER autant que par le retour — sinon elle
 * ne pourrait pas être restituée par la page de destination, qui ne connaît que
 * ce qu'on lui passe. Il en va de même de l'identifiant de retour.
 */
export function construireDetour(opts: {
  vers: string;
  destination: DestinationRetour;
  intention?: string | null;
  /** La ressource à retrouver au retour (UUID). */
  retourId?: string | null;
}): string {
  const params = new URLSearchParams();
  params.set("retour", opts.destination);
  if (opts.intention) params.set("intention", opts.intention);
  if (opts.retourId) params.set("retour_id", opts.retourId);
  return `${opts.vers}?${params.toString()}`;
}
